/**
 * api/fieldGroups.js — Phase 3 booking form designer.
 *
 * CRUD for lbs_booking_field_groups (per-tenant, per-tab named
 * groups of fields). The Field→group link is stored on
 * lbs_booking_field_overrides.group_key (Phase 1 table, extended
 * by migration 077).
 */
import { supabase } from "@/lib/supabaseClient";

export async function listFieldGroups(clientId) {
  if (!clientId) return { data: [], error: null };
  return supabase
    .from("lbs_booking_field_groups")
    .select("*")
    .eq("client_id", clientId)
    .order("tab_key", { ascending: true })
    .order("position", { ascending: true });
}

export async function upsertFieldGroup(row) {
  const payload = {
    client_id:  row.client_id,
    tab_key:    row.tab_key,
    group_key:  row.group_key,
    label:      row.label,
    position:   row.position ?? 0,
    is_visible: row.is_visible ?? true,
  };
  if (row.id) {
    return supabase
      .from("lbs_booking_field_groups")
      .update(payload)
      .eq("id", row.id)
      .select()
      .maybeSingle();
  }
  return supabase
    .from("lbs_booking_field_groups")
    .upsert(payload, { onConflict: "client_id,tab_key,group_key" })
    .select()
    .maybeSingle();
}

export async function deleteFieldGroup(id) {
  return supabase.from("lbs_booking_field_groups").delete().eq("id", id).select();
}

/**
 * Pure rendering helper: for one tab, returns an ordered list of
 * { groupKey | null, label, fields[] } buckets the engine can iterate.
 * Ungrouped fields go in a leading bucket with groupKey=null.
 */
export function bucketFieldsByGroup(fields, groups, tabKey) {
  // 1. Group defs scoped to this tab, ordered by position.
  const tabGroups = (groups || [])
    .filter((g) => g.tab_key === tabKey && g.is_visible !== false)
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  // 2. Bucket map. Use insertion order of tabGroups for stability.
  const buckets = new Map();
  buckets.set(null, { groupKey: null, label: null, fields: [] });
  for (const g of tabGroups) {
    buckets.set(g.group_key, { groupKey: g.group_key, label: g.label, fields: [] });
  }

  // 3. Drop fields into their group bucket (or "ungrouped" if no match).
  for (const f of fields) {
    if (f.tab !== tabKey) continue;
    const key = f.group_key && buckets.has(f.group_key) ? f.group_key : null;
    buckets.get(key).fields.push(f);
  }

  // 4. Sort fields within each bucket by position.
  for (const b of buckets.values()) {
    b.fields.sort((a, b2) => (a.position ?? 0) - (b2.position ?? 0));
  }

  // 5. Drop empty ungrouped bucket if there are no ungrouped fields.
  const out = [];
  for (const b of buckets.values()) {
    if (b.fields.length || b.groupKey !== null) out.push(b);
  }
  return out;
}
